import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Plus, Save, Trash2, Search, UserPlus, X } from "lucide-react";
import { adminApi } from "../../lib/api";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { useToast } from "../../hooks/use-toast";

const CATEGORIES = ["Sammler", "Limited", "Event", "Throne", "Trophäe", "Diamant", "Lipsticks", "Glitch", "Sonstige"];

const empty = { name: "", year: new Date().getFullYear(), image: "", desc: "", category: "Sammler", owner_username: "", info_html: "" };

const RaresPanel = ({ token }) => {
  const a = useMemo(() => adminApi(token), [token]);
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [form, setForm] = useState(empty);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null); // id der aktuellen Edit-Zeile

  const load = async () => {
    setLoading(true);
    try { setItems(await a.listRaresCms()); }
    catch (err) { toast({ title: "Fehler", description: err?.response?.data?.detail || "Keine Berechtigung", variant: "destructive" }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onCreate = async () => {
    if (!form.name.trim()) return;
    setCreating(true);
    try {
      await a.createRareCms({ ...form, year: parseInt(form.year || 2000, 10) });
      setForm(empty);
      toast({ title: "Rare angelegt" });
      await load();
    } catch (err) {
      toast({ title: "Fehler", description: err?.response?.data?.detail, variant: "destructive" });
    } finally { setCreating(false); }
  };

  const onSave = async (r) => {
    try {
      await a.updateRareCms(r.id, {
        name: r.name, year: parseInt(r.year || 2000, 10),
        image: r.image || "", desc: r.desc || "",
        category: r.category, owner_username: r.owner_username || null,
        info_html: r.info_html || null,
      });
      toast({ title: "Gespeichert" });
      setEditing(null);
      await load();
    } catch (err) {
      toast({ title: "Fehler", description: err?.response?.data?.detail, variant: "destructive" });
    }
  };

  const onDelete = async (r) => {
    if (!window.confirm(`Rare "${r.name}" loeschen?`)) return;
    try { await a.deleteRareCms(r.id); toast({ title: "Geloescht" }); await load(); }
    catch (err) { toast({ title: "Fehler", description: err?.response?.data?.detail, variant: "destructive" }); }
  };

  const update = (id, field, val) =>
    setItems((prev) => prev.map((x) => (x.id === id ? { ...x, [field]: val } : x)));

  const filtered = items.filter((r) =>
    !q.trim() ||
    r.name?.toLowerCase().includes(q.toLowerCase()) ||
    r.owner_username?.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div data-testid="rares-panel" className="space-y-4">
      <div>
        <h2 className="text-xl font-bold text-white">Rares-Editor</h2>
        <p className="mt-1 text-sm text-zinc-400">Erstelle, bearbeite und weise Rares konkreten Habbo-Usern als Besitzer zu.</p>
      </div>

      {/* Neues Rare */}
      <div className="rounded-xl border border-white/10 bg-[#0f141b] p-5">
        <div className="text-sm font-semibold text-white">Neues Rare anlegen</div>
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-[1.4fr_90px_120px_1fr_auto]">
          <Input
            data-testid="rare-new-name"
            placeholder="Name (z.B. 'HC Sofa')"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="bg-[#10141b] border-white/10 text-zinc-200"
          />
          <Input
            type="number"
            placeholder="Jahr"
            value={form.year}
            onChange={(e) => setForm({ ...form, year: e.target.value })}
            className="bg-[#10141b] border-white/10 text-zinc-200"
          />
          <select
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            className="rounded-md border border-white/10 bg-[#10141b] px-3 py-2 text-sm text-zinc-200"
          >
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <Input
            data-testid="rare-new-owner"
            placeholder="Besitzer-Habbo (optional)"
            value={form.owner_username}
            onChange={(e) => setForm({ ...form, owner_username: e.target.value })}
            className="bg-[#10141b] border-white/10 text-zinc-200"
          />
          <Button onClick={onCreate} disabled={creating} className="btn-primary rounded-lg px-4 text-sm" data-testid="rare-new-submit">
            {creating ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />} Anlegen
          </Button>
        </div>
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-[1fr_1fr]">
          <Input
            placeholder="Bild-URL (https://... oder /uploads/...)"
            value={form.image}
            onChange={(e) => setForm({ ...form, image: e.target.value })}
            className="bg-[#10141b] border-white/10 text-zinc-200"
          />
          <Input
            placeholder="Kurzbeschreibung"
            value={form.desc}
            onChange={(e) => setForm({ ...form, desc: e.target.value })}
            className="bg-[#10141b] border-white/10 text-zinc-200"
          />
        </div>
      </div>

      {/* Suche */}
      <div className="flex items-center gap-2">
        <Search className="h-4 w-4 text-zinc-500" />
        <Input
          placeholder="Filter: Name oder Besitzer..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-md bg-[#10141b] border-white/10 text-zinc-200"
          data-testid="rares-search"
        />
        <span className="text-xs text-zinc-500">{filtered.length} von {items.length}</span>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-zinc-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-white/10 bg-[#0f141b]">
          <div className="grid grid-cols-[56px_1.4fr_90px_140px_1fr_120px] gap-3 border-b border-white/10 bg-[#0a0d12] px-4 py-2 text-[11px] uppercase tracking-wider text-zinc-500">
            <div>Bild</div><div>Name</div><div>Jahr</div><div>Kategorie</div><div>Besitzer</div><div className="text-right">Aktionen</div>
          </div>
          {filtered.map((r) => {
            const isEditing = editing === r.id;
            return (
              <div key={r.id} data-testid={`rare-row-${r.id}`} className="grid grid-cols-[56px_1.4fr_90px_140px_1fr_120px] items-center gap-3 border-b border-white/5 px-4 py-2 last:border-b-0">
                <div className="flex justify-center">
                  {r.image ? <img src={r.image} alt={r.name} className="hb-pixel-img h-10 w-10 object-contain" /> : <div className="h-10 w-10 rounded bg-white/5" />}
                </div>
                {isEditing ? (
                  <>
                    <Input value={r.name} onChange={(e) => update(r.id, "name", e.target.value)} className="bg-[#10141b] border-white/10 text-zinc-200" />
                    <Input type="number" value={r.year} onChange={(e) => update(r.id, "year", e.target.value)} className="bg-[#10141b] border-white/10 text-zinc-200" />
                    <select value={r.category} onChange={(e) => update(r.id, "category", e.target.value)} className="rounded-md border border-white/10 bg-[#10141b] px-2 py-1.5 text-sm text-zinc-200">
                      {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <div className="flex items-center gap-1">
                      <UserPlus className="h-3.5 w-3.5 text-zinc-500" />
                      <Input
                        value={r.owner_username || ""}
                        onChange={(e) => update(r.id, "owner_username", e.target.value)}
                        placeholder="Habbo-Name"
                        className="bg-[#10141b] border-white/10 text-zinc-200"
                        data-testid={`rare-owner-${r.id}`}
                      />
                    </div>
                    <div className="flex items-center justify-end gap-1">
                      <Button onClick={() => onSave(r)} className="btn-primary rounded-lg px-2 py-1 text-xs" data-testid={`rare-save-${r.id}`}><Save className="h-4 w-4" /></Button>
                      <Button onClick={() => { setEditing(null); load(); }} className="btn-ghost rounded-lg px-2 py-1 text-xs"><X className="h-4 w-4" /></Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="truncate text-sm font-semibold text-white" title={r.name}>{r.name}</div>
                    <div className="text-sm text-zinc-300">{r.year}</div>
                    <div className="text-xs text-zinc-400">{r.category}</div>
                    <div className="text-sm">
                      {r.owner_username ? (
                        <a href={`/u/${encodeURIComponent(r.owner_username)}`} className="inline-flex items-center gap-1 text-[#b39bff] hover:text-white">
                          <img src={`https://www.habbo.de/habbo-imaging/avatarimage?user=${encodeURIComponent(r.owner_username)}&size=s&headonly=1`} alt="" className="hb-pixel-img h-5 w-5" />
                          {r.owner_username}
                        </a>
                      ) : <span className="text-xs text-zinc-600 italic">Kein Besitzer</span>}
                    </div>
                    <div className="flex items-center justify-end gap-1">
                      <Button onClick={() => setEditing(r.id)} className="btn-ghost rounded-lg px-2 py-1 text-xs" data-testid={`rare-edit-${r.id}`}>Bearbeiten</Button>
                      <Button onClick={() => onDelete(r)} className="btn-ghost rounded-lg px-2 py-1 text-xs text-rose-300 hover:border-rose-500/40"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="px-4 py-10 text-center text-sm text-zinc-500">Keine Rares gefunden.</div>
          )}
        </div>
      )}

      {/* Erweiterte Bearbeitung (Description + Info-HTML) fuer den aktuell editierten Eintrag */}
      {editing && (() => {
        const r = items.find((x) => x.id === editing);
        if (!r) return null;
        return (
          <div className="rounded-xl border border-[#7c4dff]/40 bg-[#0f141b] p-5">
            <div className="mb-2 text-sm font-semibold text-white">Erweiterte Bearbeitung: <span className="text-[#b39bff]">{r.name}</span></div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-[11px] uppercase tracking-wider text-zinc-500">Bild-URL</label>
                <Input value={r.image || ""} onChange={(e) => update(r.id, "image", e.target.value)} className="bg-[#10141b] border-white/10 text-zinc-200" data-testid={`rare-image-${r.id}`} />
              </div>
              <div>
                <label className="mb-1 block text-[11px] uppercase tracking-wider text-zinc-500">Kurzbeschreibung</label>
                <Input value={r.desc || ""} onChange={(e) => update(r.id, "desc", e.target.value)} className="bg-[#10141b] border-white/10 text-zinc-200" data-testid={`rare-desc-${r.id}`} />
              </div>
              <div className="md:col-span-2">
                <label className="mb-1 block text-[11px] uppercase tracking-wider text-zinc-500">Info-HTML (optional, ausfuehrlicher Text)</label>
                <Textarea
                  value={r.info_html || ""}
                  onChange={(e) => update(r.id, "info_html", e.target.value)}
                  rows={5}
                  placeholder="<p>Geschichte des Rares...</p>"
                  className="bg-[#10141b] border-white/10 text-zinc-200 font-mono text-xs"
                  data-testid={`rare-info-${r.id}`}
                />
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default RaresPanel;
